import {
  Button,
  Icon16Alert,
  Icon16Checkmark,
  Icon16Close,
  Icon16Edit,
  Icon16Info,
  Icon24ArrowRight,
  IconButton,
  Typography,
} from "@cur8/maneki";
import { useQueryClient } from "@tanstack/react-query";
import {
  BarcodeAssignmentMode,
  useBarcodeAssignmentMutation,
} from "hooks/mutations/useBarcodeAssignmentMutation";
import { useVisitBloodSamplesQuery } from "hooks/queries/useVisitBloodSamplesQuery";
import { CheckInParams } from "lib/types/checkInParameters";
import { ComponentType, useCallback, useMemo, useState } from "react";
import { paths } from "render/routes/paths";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent/PageFrameContent";
import { BarcodeScanner } from "render/ui/presentation/BarcodeScanner";
import { List } from "render/ui/presentation/List";
import { barcodeAssignmentErrorMessage } from "./errorMessage";
import {
  assignedVialTypes,
  decideScanAction,
  vialTypeLabels,
  VialType,
} from "./scanDecision";
import styles from "./styles.module.sass";

type AssignVialsProps = {
  goTo(url: string): void;
} & CheckInParams;

type Banner =
  | { kind: "error"; message: string }
  | { kind: "info"; message: string }
  | { kind: "success"; message: string };

const BANNER_ICON: Record<Banner["kind"], ComponentType> = {
  success: Icon16Checkmark,
  info: Icon16Info,
  error: Icon16Alert,
};

const PAST_TENSE: Record<BarcodeAssignmentMode, string> = {
  assign: "assigned",
  reassign: "re-assigned",
};

function needsReassignModeMessage(scanned: VialType) {
  return `${vialTypeLabels[scanned]} vial already assigned. Tap the edit icon next to it to re-assign.`;
}

function wrongVialForReassignMessage(reassigning: VialType, scanned: VialType) {
  return `You're re-assigning ${vialTypeLabels[reassigning]}, but scanned a ${vialTypeLabels[scanned]} barcode.`;
}

export function AssignVials({ goTo, patientId, visitId }: AssignVialsProps) {
  const queryClient = useQueryClient();
  const barcodeMutation = useBarcodeAssignmentMutation();
  const bloodSamplesQuery = useVisitBloodSamplesQuery({ patientId, visitId });
  const [banner, setBanner] = useState<Banner | undefined>();
  const [reassigningType, setReassigningType] = useState<
    VialType | undefined
  >();
  const [scanToken, setScanToken] = useState(0);

  const rearm = useCallback(() => setScanToken((n) => n + 1), []);

  const assignedByType = useMemo(
    () => assignedVialTypes(bloodSamplesQuery.data?.bloodSamples),
    [bloodSamplesQuery.data]
  );

  const allAssigned =
    assignedByType.wholeBlood != null && assignedByType.plasma != null;

  const mutationPending = barcodeMutation.isPending;
  const pendingMode = mutationPending
    ? barcodeMutation.variables?.mode
    : undefined;

  const toggleReassign = useCallback((type: VialType) => {
    setBanner(undefined);
    setReassigningType((current) => (current === type ? undefined : type));
  }, []);

  const runBarcodeMutation = useCallback(
    (mode: BarcodeAssignmentMode, vialType: VialType, barcodeId: string) => {
      barcodeMutation.mutate(
        { patientId, visitId, barcodeId, mode },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["visitBloodSamples", patientId, visitId],
            });
            setReassigningType((current) =>
              current === vialType ? undefined : current
            );
            setBanner({
              kind: "success",
              message: `${vialTypeLabels[vialType]} vial ${PAST_TENSE[mode]} to ${barcodeId}.`,
            });
            rearm();
          },
          onError: (err: unknown) => {
            setBanner({
              kind: "error",
              message: barcodeAssignmentErrorMessage(err, mode, barcodeId),
            });
            rearm();
          },
        }
      );
    },
    [barcodeMutation, patientId, queryClient, rearm, visitId]
  );

  const handleScan = useCallback(
    (barcodeId: string) => {
      setBanner(undefined);

      const decision = decideScanAction({
        barcodeId,
        assignedByType,
        reassigningType,
      });

      switch (decision.kind) {
        case "invalidPrefix":
          setBanner({
            kind: "error",
            message: `Invalid barcode prefix: "${decision.prefix}". Expected a whole blood (W2) or plasma (L2) barcode.`,
          });
          rearm();
          return;

        case "alreadyAssigned":
          setBanner({
            kind: "info",
            message: `${
              vialTypeLabels[decision.vialType]
            } vial already assigned to ${decision.barcodeId}.`,
          });
          rearm();
          return;

        case "needsReassignMode":
          setBanner({
            kind: "error",
            message: needsReassignModeMessage(decision.vialType),
          });
          rearm();
          return;

        case "wrongVialForReassign":
          setBanner({
            kind: "error",
            message: wrongVialForReassignMessage(
              decision.reassigning,
              decision.scanned
            ),
          });
          rearm();
          return;

        case "assign":
          runBarcodeMutation("assign", decision.vialType, decision.barcodeId);
          return;

        case "reassign":
          runBarcodeMutation(
            "reassign",
            decision.vialType,
            decision.newBarcodeId
          );
          return;
      }
    },
    [assignedByType, reassigningType, rearm, runBarcodeMutation]
  );

  const handleScanError = useCallback(
    (err: Error) => {
      setBanner({ kind: "error", message: `Camera error: ${err.message}` });
      rearm();
    },
    [rearm]
  );

  const goBack = useCallback(() => {
    goTo(paths.checkInSuccess.url({ patientId, visitId }));
  }, [goTo, patientId, visitId]);

  // Keep the camera mounted while a mutation is in flight. The decoder is
  // already silenced after a scan until `rearm()` (post-mutation) bumps
  // `scanToken`, so leaving `active` true here doesn't risk duplicate scans —
  // it just avoids tearing down `getUserMedia` on every assignment.
  const scannerActive = !allAssigned || reassigningType != null;

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <Typography variant="display-s">
              Scan blood vials (Sangra only)
            </Typography>
            <Typography variant="body-m" color="subtle">
              Only complete this step for Sangra blood draws. If this visit isn
              {"’"}t a Sangra scan, skip it by tapping Done without scanning any
              vials.
            </Typography>

            <BarcodeScanner
              onScan={handleScan}
              onError={handleScanError}
              active={scannerActive}
              resetToken={scanToken}
            />

            {banner ? <BannerView banner={banner} /> : undefined}

            {pendingMode ? (
              <div className={styles.statusText}>
                <Typography variant="body-m" color="subtle">
                  {pendingMode === "reassign"
                    ? "Re-assigning barcode..."
                    : "Assigning barcode..."}
                </Typography>
              </div>
            ) : undefined}

            <List>
              <List.Item>
                <VialRow
                  type="wholeBlood"
                  assignedBarcode={assignedByType.wholeBlood}
                  reassigning={reassigningType === "wholeBlood"}
                  disabled={mutationPending}
                  onToggleReassign={toggleReassign}
                />
              </List.Item>
              <List.Divider />
              <List.Item>
                <VialRow
                  type="plasma"
                  assignedBarcode={assignedByType.plasma}
                  reassigning={reassigningType === "plasma"}
                  disabled={mutationPending}
                  onToggleReassign={toggleReassign}
                />
              </List.Item>
            </List>
          </section>
        }
        buttons={
          <Button onPress={goBack} fillContainer icon={<Icon24ArrowRight />}>
            Done
          </Button>
        }
      />
    </PageFrameContent>
  );
}

function BannerView({ banner }: { banner: Banner }) {
  const Icon = BANNER_ICON[banner.kind];
  return (
    <div className={styles.banner} data-kind={banner.kind}>
      <Icon />
      <Typography variant="body-m">{banner.message}</Typography>
    </div>
  );
}

function VialRow({
  type,
  assignedBarcode,
  reassigning,
  disabled,
  onToggleReassign,
}: {
  type: VialType;
  assignedBarcode: string | undefined;
  reassigning: boolean;
  disabled: boolean;
  onToggleReassign(type: VialType): void;
}) {
  const assigned = assignedBarcode != null;
  return (
    <div className={styles.vialItem}>
      <Typography variant="title-s">{vialTypeLabels[type]}</Typography>
      <div className={styles.vialActions}>
        {assigned ? (
          <IconButton
            variant="secondary"
            disabled={disabled}
            onPress={() => onToggleReassign(type)}
            aria-label={
              reassigning
                ? `Cancel re-assigning ${vialTypeLabels[type]}`
                : `Re-assign ${vialTypeLabels[type]}`
            }
          >
            {reassigning ? <Icon16Close /> : <Icon16Edit />}
          </IconButton>
        ) : undefined}
        <VialStatusBadge assigned={assigned} reassigning={reassigning} />
      </div>
    </div>
  );
}

type VialStatus = "pending" | "assigned" | "reassigning";

const VIAL_STATUS_LABELS: Record<VialStatus, string> = {
  pending: "Pending",
  assigned: "Assigned",
  reassigning: "Re-assigning",
};

function getVialStatus(assigned: boolean, reassigning: boolean): VialStatus {
  if (reassigning) {
    return "reassigning";
  }
  if (assigned) {
    return "assigned";
  }
  return "pending";
}

function VialStatusBadge({
  assigned,
  reassigning,
}: {
  assigned: boolean;
  reassigning: boolean;
}) {
  const status = getVialStatus(assigned, reassigning);
  return (
    <div
      className={styles.vialStatusBadge}
      data-assigned={assigned}
      data-reassigning={reassigning}
    >
      {status === "assigned" ? <Icon16Checkmark /> : undefined}
      <Typography variant="eyebrow-m" as="span">
        {VIAL_STATUS_LABELS[status]}
      </Typography>
    </div>
  );
}
